import { useEffect, useRef } from 'react'
import cable from '@/lib/cable'
import type { ConversationListItem } from '@/components/inbox/types'

export interface EndUserOnlineStatus {
  end_user_id: number
  online: boolean
  last_seen_at: string
}

export interface AgentPresenceData {
  conversation_id: number
  agent_id: number
  agent: { id: number; name: string; avatar_url: string | null } | null
}

interface BroadcastData {
  type: 'conversation_updated' | 'end_user_online_status' | 'agent_presence'
  conversation?: ConversationListItem
  end_user_id?: number
  online?: boolean
  last_seen_at?: string
  conversation_id?: number
  agent_id?: number
  agent?: { id: number; name: string; avatar_url: string | null } | null
}

interface UseInboxChannelProps {
  onConversationUpdated: (conversation: ConversationListItem) => void
  onEndUserOnlineStatusChanged?: (data: EndUserOnlineStatus) => void
  onAgentPresence?: (data: AgentPresenceData) => void
  onReconnect?: () => void
  enabled?: boolean
}

export function useInboxChannel({ onConversationUpdated, onEndUserOnlineStatusChanged, onAgentPresence, onReconnect, enabled = true }: UseInboxChannelProps) {
  const subscriptionRef = useRef<ReturnType<typeof cable.subscriptions.create> | null>(null)
  const onConversationUpdatedRef = useRef(onConversationUpdated)
  const onEndUserOnlineStatusChangedRef = useRef(onEndUserOnlineStatusChanged)
  const onAgentPresenceRef = useRef(onAgentPresence)
  const onReconnectRef = useRef(onReconnect)
  const hasConnectedRef = useRef(false)

  useEffect(() => {
    onConversationUpdatedRef.current = onConversationUpdated
    onEndUserOnlineStatusChangedRef.current = onEndUserOnlineStatusChanged
    onAgentPresenceRef.current = onAgentPresence
    onReconnectRef.current = onReconnect
  }, [onConversationUpdated, onEndUserOnlineStatusChanged, onAgentPresence, onReconnect])

  useEffect(() => {
    if (!enabled) return

    const subscription = cable.subscriptions.create(
      { channel: 'InboxChannel' },
      {
        connected() {
          // On reconnection, events were likely missed while offline:
          // trigger a resync so the client converges with the server.
          if (hasConnectedRef.current) {
            onReconnectRef.current?.()
          }
          hasConnectedRef.current = true
        },
        disconnected() {
          // Disconnected from inbox channel
        },
        received(data: BroadcastData) {
          switch (data.type) {
            case 'conversation_updated':
              if (data.conversation) {
                onConversationUpdatedRef.current(data.conversation)
              }
              break
            case 'end_user_online_status':
              if (data.end_user_id !== undefined && onEndUserOnlineStatusChangedRef.current) {
                onEndUserOnlineStatusChangedRef.current({
                  end_user_id: data.end_user_id,
                  online: data.online ?? false,
                  last_seen_at: data.last_seen_at ?? '',
                })
              }
              break
            case 'agent_presence':
              if (data.conversation_id !== undefined && data.agent_id !== undefined && onAgentPresenceRef.current) {
                onAgentPresenceRef.current({
                  conversation_id: data.conversation_id,
                  agent_id: data.agent_id,
                  agent: data.agent ?? null,
                })
              }
              break
          }
        },
      }
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
    }
  }, [enabled])

  return {}
}
